import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './Periods.css';

function Periods() {
  const [periods, setPeriods] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [filter, setFilter] = useState('open'); // 'open' or 'all'
  const user = auth.currentUser;

  // Load periods
  useEffect(() => {
    if (!user) return;
    const periodsRef = collection(db, 'users', user.uid, 'Accounting_Periods');
    const unsubscribe = onSnapshot(periodsRef, (snapshot) => {
      const periodsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort periods by date (most recent first)
      periodsData.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });
      
      setPeriods(periodsData);
    });
    return unsubscribe;
  }, [user]);

  // Filter periods based on selected filter
  const filteredPeriods = filter === 'open' 
    ? periods.filter(p => p.isOpen === true)
    : periods;

  const handleEdit = (period) => {
    setEditingPeriod(period);
    setShowAddDialog(true);
  };

  const handleToggleStatus = async (period) => {
    try {
      await updateDoc(doc(db, 'users', user.uid, 'Accounting_Periods', period.id), {
        isOpen: !period.isOpen
      });
    } catch (err) {
      alert('Error updating period status: ' + err.message);
    }
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setEditingPeriod(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatPeriodName = (dateString) => {
    if (!dateString) return '-';
    const [year, month] = dateString.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  return (
    <div className="section-content">
      <div className="section-header">
        <h2>Accounting Periods</h2>
        <div className="header-actions">
          <select 
            className="filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="open">Open</option>
            <option value="all">All Periods</option>
          </select>
          <button className="btn-primary" onClick={() => setShowAddDialog(true)}>
            + Add Period
          </button>
        </div>
      </div>

      {filteredPeriods.length === 0 ? (
        <p className="empty-state">No periods found.</p>
      ) : (
        <div className="periods-list">
          {filteredPeriods.map((period) => (
            <div key={period.id} className="period-card">
              <div className="period-info">
                <div className="period-header">
                  <h3 className="period-title">{formatPeriodName(period.date)}</h3>
                  <span className={`status-badge ${period.isOpen ? 'open' : 'closed'}`}>
                    {period.isOpen ? '🔓 Open' : '🔒 Closed'}
                  </span>
                </div>
                <div className="period-date-display">
                  <span className="date-label">Date:</span>
                  <span className="date-value">{period.date}</span>
                </div>
              </div>
              <div className="period-actions">
                <button 
                  className={`btn-toggle-status ${period.isOpen ? 'open' : 'closed'}`}
                  onClick={() => handleToggleStatus(period)}
                  title={period.isOpen ? 'Close Period' : 'Open Period'}
                >
                  {period.isOpen ? '🔒 Close' : '🔓 Open'}
                </button>
                <button className="btn-edit" onClick={() => handleEdit(period)}>
                  Edit
                </button>
                <button className="btn-delete-disabled" disabled>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddDialog && (
        <AddPeriodDialog 
          isOpen={showAddDialog}
          onClose={handleCloseDialog}
          editPeriod={editingPeriod}
        />
      )}
    </div>
  );
}

// Add Period Dialog Component
function AddPeriodDialog({ isOpen, onClose, editPeriod }) {
  const [date, setDate] = useState('');
  const [isOpenState, setIsOpenState] = useState(true);
  const user = auth.currentUser;

  useEffect(() => {
    if (editPeriod) {
      // Extract YYYY-MM from the date for editing
      const [year, month] = editPeriod.date.split('-');
      setDate(`${year}-${month}`);
      setIsOpenState(editPeriod.isOpen !== undefined ? editPeriod.isOpen : true);
    } else {
      // Set default to current month (YYYY-MM format)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      setDate(`${year}-${month}`);
      setIsOpenState(true);
    }
  }, [editPeriod]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      alert('Please select a period');
      return;
    }

    // Validate date format YYYY-MM
    const dateRegex = /^\d{4}-\d{2}$/;
    if (!dateRegex.test(date)) {
      alert('Period must be in YYYY-MM format');
      return;
    }

    try {
      // Create doc ID and full date
      const [year, month] = date.split('-');
      const docId = `PER-${year}-${month}`;
      const fullDate = `${year}-${month}-01`; // Always use day 01

      const periodData = {
        date: fullDate,
        isOpen: isOpenState
      };

      if (editPeriod) {
        // Update existing period
        await updateDoc(doc(db, 'users', user.uid, 'Accounting_Periods', editPeriod.id), periodData);
      } else {
        // Check if period already exists
        const periodRef = doc(db, 'users', user.uid, 'Accounting_Periods', docId);
        const periodSnap = await getDoc(periodRef);
        
        if (periodSnap.exists()) {
          alert(`Period ${year}-${month} already exists!`);
          return;
        }

        // Create new period with custom doc ID
        await setDoc(periodRef, periodData);
      }

      onClose();
    } catch (err) {
      alert('Error saving period: ' + err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>{editPeriod ? 'Edit Period' : 'Add New Period'}</h3>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Period (Month & Year) *</label>
            <input
              type="month"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
            <div className="help-text">
              The date will be stored as YYYY-MM-01
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={isOpenState}
                onChange={(e) => setIsOpenState(e.target.checked)}
              />
              <span>Period is Open (allow transactions)</span>
            </label>
          </div>

          <div className="dialog-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              {editPeriod ? 'Update Period' : 'Create Period'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Periods;